import React from 'react';
import assets from '../assets';
import {
  Navigate,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSupplierSurvey } from '../hooks';
import PageLoading from '../../../components/PageLoading';
import SomethingWentWrong from '../../../components/SomethingWentWrong';
import SurveyContainer from '../../../components/Survey/Container';
import { Logo } from '../../../components/Survey/icons';
import ImageHeader from '../../../components/Survey/ImageHeader';
import { StyledContainer } from '../../../views/components/QuestionnaireV3/Question/index.styles';
import Title from '../../../components/Survey/Title';
import { toast } from 'react-toastify';
import { Box, FormControlLabel, Radio, Typography } from '@mui/material';
import { GhgSupplierSurveyAnswerInSchema } from 'src/Ghg/interfaces';
import {
  ErrorBuilder,
  extractSchemaErrors,
  mapError,
  SchemaErrors,
} from 'src/utils/validation';
import TextField from 'src/components/TextField';
import SingleFileUpload from 'src/components/SingleFileUpload';
import { registrationTokenParam } from '../utils';

type FormState = GhgSupplierSurveyAnswerInSchema;
type FormErrors = SchemaErrors<FormState>;

export const validateForm = (data: Partial<FormState>): FormErrors => {
  const builder = new ErrorBuilder(data)
    .checkNonNull(['has_sedex_questionnaire'], 'validation.required')
    .checkRule(
      ['zc_code'],
      (value, schema) => {
        return (
          !schema.has_sedex_questionnaire || (value !== null && value !== '')
        );
      },
      'validation.required'
    )
    .checkRule(
      ['saq_file'],
      (value, schema) => {
        return !schema.has_sedex_questionnaire || value !== null;
      },
      'ghg.supplierSurvey.firstStep.saqFileRequired'
    );

  return builder.build();
};

export default function SupplierSurveyFirstStep() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useParams();
  const survey = useSupplierSurvey();
  const location = useLocation();

  const [searchParams, _] = useSearchParams();
  const registrationToken = searchParams.get('registration_token');

  const [form, setForm] = React.useState<FormState>({
    company_name: '',
    vat_identifier: '',
    contact_email: '',
    has_sedex_questionnaire: null,
    wants_to_use_platform: null,
    zc_code: null,
    saq_file: null,
  });

  const [errors, setErrors] = React.useState<FormErrors>({});

  React.useEffect(() => {
    const answer = survey.data?.answer;
    if (answer) {
      setForm({
        company_name: form.company_name || answer.company_name,
        vat_identifier: form.vat_identifier || answer.vat_identifier,
        contact_email: form.contact_email || answer.contact_email,
        has_sedex_questionnaire:
          form.has_sedex_questionnaire ?? answer.has_sedex_questionnaire,
        wants_to_use_platform:
          form.wants_to_use_platform ?? answer.wants_to_use_platform,
        zc_code: form.zc_code ?? answer.zc_code,
        saq_file: form.saq_file ?? answer.saq_file,
      });
    }
  }, [survey.data]);

  const sendResponse = () =>
    survey.update
      .mutateAsync(form)
      .then((data) => {
        if (data.data?.answer?.has_sedex_questionnaire) {
          navigate(`../main-page${registrationTokenParam(registrationToken)}`);
        } else if (data.data?.answer?.has_sedex_questionnaire === false) {
          navigate(
            `../questionnaire-esg${registrationTokenParam(registrationToken)}`
          );
        }
      })
      .catch((err) => {
        setErrors(extractSchemaErrors(err, form));
        toast.error(String(t('ghg.supplierSurvey.submitError')));
      });

  if (survey.isError) return <SomethingWentWrong />;
  if (!survey.data) return <PageLoading />;
  if (!survey.data.answer) {
    return <Navigate to={`../initial-questions${location.search}`} replace />;
  }

  const prevLink = {
    onClick: () => {
      navigate(`../main-page${registrationTokenParam(registrationToken)}`);
    },
    label: 'ghg.supplierSurvey.back',
  };

  const nextLinkLabel = form.has_sedex_questionnaire
    ? 'ghg.supplierSurvey.send'
    : 'ghg.supplierSurvey.next';

  const nextLink = {
    label: nextLinkLabel,
    onClick: () => {
      const errors = validateForm(form);
      setErrors(errors);
      if (Object.keys(errors).length > 0) {
        toast.error(String(t(Object.values(errors)[0].message)));
        return;
      }
      sendResponse();
    },
  };

  return (
    <SurveyContainer
      logo={<Logo />}
      header={
        <ImageHeader
          imageSrc={assets.questionnaire.image}
          text={t('ghg.supplierSurvey.mainHeader')}
          small
        />
      }
      prevLink={prevLink}
      nextLink={form.has_sedex_questionnaire !== null ? nextLink : undefined}
    >
      <Title title={t('ghg.supplierSurvey.firstStep.title')} />
      <StyledContainer>
        <FormControlLabel
          label={t('ghg.supplierSurvey.firstStep.label01')}
          control={
            <Radio
              checked={form.has_sedex_questionnaire === true}
              onClick={() =>
                setForm({ ...form, has_sedex_questionnaire: true })
              }
            />
          }
        />
        <FormControlLabel
          sx={{ mt: '16px' }}
          label={t('ghg.supplierSurvey.firstStep.label02')}
          control={
            <Radio
              checked={form.has_sedex_questionnaire === false}
              onClick={() =>
                setForm({ ...form, has_sedex_questionnaire: false })
              }
            />
          }
        />
        {form.has_sedex_questionnaire && (
          <>
            <TextField
              required
              sx={{ mt: '16px', pb: '16px' }}
              fullWidth
              label={t('ghg.supplierSurvey.firstStep.zc_code')}
              value={form.zc_code || ''}
              onChange={(e) => setForm({ ...form, zc_code: e.target.value })}
              errorText={mapError(errors, 'zc_code', form.zc_code)}
            />
            <SingleFileUpload
              attachButtonText={t('ghg.supplierSurvey.firstStep.attachment')}
              url={`web/ghg-suppliers/survey/answer/${token}/attachments`}
              file={form.saq_file}
              onSuccess={(saq_file) => setForm({ ...form, saq_file })}
            />
            {errors.saq_file && (
              <Box mt="16px" color="error.main">
                <Typography variant="body2">
                  {t(errors.saq_file.message)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </StyledContainer>
    </SurveyContainer>
  );
}
